import { KpiCardValue, KpiValue } from '@app/components/kpi';
import { Box } from '@mui/material';
import { defineMessages, useIntl } from 'react-intl';
import { QUALITY_ICONS } from '../consts';
import { useQualityStatusColors } from '../useQualityStatusColors';
import { qualityReportColorForText } from '../helpers/quality-report-color';
import { getQualityKpiSnapshotFromReport } from './map-quality-kpis-to-cards';
import { StreamQualityReportResponseDto } from '../stream-quality.types';

const kpiTitleMessages = defineMessages({
  healthScore: {
    id: 'logger.reports.streamQuality.kpis.healthScore',
    defaultMessage: 'Health Score',
  },
  stationsWithIncidents: {
    id: 'logger.reports.streamQuality.kpis.stationsWithIncidents',
    defaultMessage: 'Emisoras con incidentes',
  },
  stationsWithContinuityIncidents: {
    id: 'logger.reports.streamQuality.kpis.stationsWithContinuityIncidents',
    defaultMessage: 'Emisoras con incidentes de continuidad',
  },
  stationsWithQualityIncidents: {
    id: 'logger.reports.streamQuality.kpis.stationsWithQualityIncidents',
    defaultMessage: 'Emisoras con incidentes de calidad',
  },
  continuityIncidentsTotal: {
    id: 'logger.reports.streamQuality.kpis.continuityIncidentsTotal',
    defaultMessage: 'Incidentes de continuidad',
  },
  continuityIncidentDurationAvg: {
    id: 'logger.reports.streamQuality.kpis.continuityIncidentDurationAvg',
    defaultMessage: 'Duración media de incidentes de continuidad',
  },
  qualityIncidentsTotal: {
    id: 'logger.reports.streamQuality.kpis.qualityIncidentsTotal',
    defaultMessage: 'Incidentes de calidad',
  },
  qualityIncidentDurationAvg: {
    id: 'logger.reports.streamQuality.kpis.qualityIncidentDurationAvg',
    defaultMessage: 'Duración media de incidentes de calidad',
  },
  qualityAvgNotApplicable: {
    id: 'logger.reports.streamQuality.kpis.qualityAvgNotApplicable',
    defaultMessage: '—',
  },
});

export type KpiCardsProps = {
  report: StreamQualityReportResponseDto;
};

const kpiRowGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
  },
} as const;

/**
 * KPI strip for a stream quality report: primary row (health + affected stations) and a denser
 * secondary row (`size="sm"`) for incident aggregates.
 */
export function KpiCards({ report }: KpiCardsProps) {
  const { $t, locale } = useIntl();
  const { minimapGradientStops, kpiIncidentGradientStops } = useQualityStatusColors();
  const snapshot = getQualityKpiSnapshotFromReport(report);

  if (snapshot == null) {
    return null;
  }

  const healthScorePercent = snapshot.healthScoreRatio * 100;
  const totalStations = snapshot.totalStations;

  const kpiIncidentColor = (rate0to1: number) =>
    qualityReportColorForText(rate0to1, kpiIncidentGradientStops);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      <Box sx={kpiRowGridSx}>
        <KpiCardValue
          icon={<QUALITY_ICONS.healthScore fontSize="inherit" color="action" />}
          title={$t(kpiTitleMessages.healthScore)}
          value={
            <KpiValue
              type="number"
              locale={locale}
              value={healthScorePercent}
              maximumFractionDigits={1}
              color={qualityReportColorForText(snapshot.healthScoreRatio, minimapGradientStops)}
            />
          }
        />
        <KpiCardValue
          icon={<QUALITY_ICONS.stationsWithIncidents fontSize="inherit" color="action" />}
          title={$t(kpiTitleMessages.stationsWithIncidents)}
          value={
            <KpiValue
              type="ratio"
              locale={locale}
              numerator={snapshot.stationsWithAnyIssueCount}
              denominator={totalStations}
              color={kpiIncidentColor(1 - snapshot.stationsWithAnyIssueCount / totalStations)}
            />
          }
        />
        <KpiCardValue
          icon={<QUALITY_ICONS.gap fontSize="inherit" color="action" />}
          title={$t(kpiTitleMessages.stationsWithContinuityIncidents)}
          value={
            <KpiValue
              type="ratio"
              locale={locale}
              numerator={snapshot.continuityAffectedStationsCount}
              denominator={totalStations}
              color={kpiIncidentColor(1 - snapshot.continuityAffectedStationsCount / totalStations)}
            />
          }
        />
        <KpiCardValue
          icon={<QUALITY_ICONS.quality fontSize="inherit" color="action" />}
          title={$t(kpiTitleMessages.stationsWithQualityIncidents)}
          value={
            <KpiValue
              type="ratio"
              locale={locale}
              numerator={snapshot.qualityAffectedStationsCount}
              denominator={totalStations}
              color={kpiIncidentColor(1 - snapshot.qualityAffectedStationsCount / totalStations)}
            />
          }
        />
      </Box>
      <Box sx={kpiRowGridSx}>
        <KpiCardValue
          icon={<QUALITY_ICONS.gap fontSize="inherit" color="action" />}
          size="sm"
          title={$t(kpiTitleMessages.continuityIncidentsTotal)}
          value={
            <KpiValue
              type="number"
              locale={locale}
              value={snapshot.continuityIncidentCount}
              color={'secondary.dark'}
            />
          }
        />
        <KpiCardValue
          icon={<QUALITY_ICONS.gap fontSize="inherit" color="action" />}
          size="sm"
          title={$t(kpiTitleMessages.continuityIncidentDurationAvg)}
          value={
            <KpiValue
              type="duration"
              locale={locale}
              valueSeconds={snapshot.continuityMeanIncidentDurationSeconds}
              color={'secondary.dark'}
            />
          }
        />
        <KpiCardValue
          icon={<QUALITY_ICONS.quality fontSize="inherit" color="action" />}
          size="sm"
          title={$t(kpiTitleMessages.qualityIncidentsTotal)}
          value={
            <KpiValue
              type="number"
              locale={locale}
              value={snapshot.qualityTotalIncidentCount}
              color={'secondary.dark'}
            />
          }
        />
        <KpiCardValue
          icon={<QUALITY_ICONS.quality fontSize="inherit" color="action" />}
          size="sm"
          title={$t(kpiTitleMessages.qualityIncidentDurationAvg)}
          value={
            snapshot.qualityMeanIncidentDurationSeconds != null ? (
              <KpiValue
                type="duration"
                locale={locale}
                valueSeconds={snapshot.qualityMeanIncidentDurationSeconds}
                color={'secondary.dark'}
              />
            ) : (
              $t(kpiTitleMessages.qualityAvgNotApplicable)
            )
          }
        />
      </Box>
    </Box>
  );
}
